import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, media } from '../styles/theme';

export interface FaqAccordionItem {
  question: string;
  answer: React.ReactNode;
}

const AccordionItem = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.sm};
  overflow: hidden;
`;

const AccordionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background-color: ${(props) =>
    props.isOpen ? theme.colors.gray.lightest : theme.colors.white};
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text};
  transition: background-color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.gray.lightest};
  }

  ${media.mobile} {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.fontSizes.sm};
  }
`;

const AccordionIcon = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  font-size: 1.25rem;
  transition: transform ${theme.transitions.fast};
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
  margin-left: ${theme.spacing.sm};
`;

const AccordionContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  max-height: ${(props) => (props.isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height ${theme.transitions.normal};
`;

const AccordionBody = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  line-height: 1.7;
  color: ${theme.colors.text};

  p {
    margin-bottom: 1rem;
  }

  p:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ${media.mobile} {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`;

// Collapsed answers stay in the DOM (max-height collapse), so prerendered HTML
// contains the full FAQ text that the FAQPage schema must match.
const FaqAccordion: React.FC<{ items: FaqAccordionItem[] }> = ({ items }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {items.map((item, index) => (
        <AccordionItem key={index}>
          <AccordionButton
            isOpen={openIndex === index}
            onClick={() => toggle(index)}
          >
            {item.question}
            <AccordionIcon isOpen={openIndex === index}>▾</AccordionIcon>
          </AccordionButton>
          <AccordionContent isOpen={openIndex === index}>
            <AccordionBody>{item.answer}</AccordionBody>
          </AccordionContent>
        </AccordionItem>
      ))}
    </>
  );
};

export default FaqAccordion;
